import { Link } from 'react-router-dom'
import { Calendar, CheckSquare, Users, Gift, Shirt, Heart, Camera, DollarSign, Tv, Globe, Mail, Feather, Sparkles } from 'lucide-react'
import MarketingHeader from '../components/MarketingHeader'
import MarketingFooter from '../components/MarketingFooter'
import LaunchHeader from '../components/LaunchHeader'
import LaunchFooter from '../components/LaunchFooter'
import { useSiteLive } from '../lib/launch'

const FEATURES = [
  {
    category:'PLANNING', icon:Calendar, color:'#9BAF9B', bg:'rgba(155,175,155,0.15)',
    title:'Multi-Event Manager',
    desc:'Traditional, white wedding, reception, court — manage every event in one place with separate guest lists, timelines, and programmes.',
    bullets:['Add unlimited events per wedding','Each event has its own guest list and RSVP','Mark an event "Strictly by Invitation" — its venue is hidden from the public portal and shared only on each guest\'s personal invite','Days countdown per event','Google Maps directions to each venue for guests'],
  },
  {
    category:'PLANNING', icon:CheckSquare, color:'#C4826A', bg:'rgba(196,130,106,0.12)',
    title:'Smart To-Do Checklist',
    desc:'A pre-built wedding checklist organized by how many months you have left. It knows where you are in the timeline and tells you what to focus on right now.',
    bullets:['Tasks organized from 12+ months out to final week','Current timeline bucket highlighted automatically','Add your own custom tasks to any bucket','Tasks link directly to the relevant app feature','Progress tracked across all 27 pre-built tasks plus any custom tasks you add'],
  },
  {
    category:'GUESTS', icon:Users, color:'#9BAF9B', bg:'rgba(155,175,155,0.15)',
    title:'Smart Guest & RSVP',
    desc:'Know exactly who is coming to which event. Every guest gets a unique personal RSVP link via WhatsApp — no one can forward it to an uninvited guest.',
    bullets:['Per-event guest lists with individual RSVP tracking','Dietary preferences and meal selection per guest','Bulk WhatsApp invite sending from the dashboard','QR code check-in on the wedding day','Export full guest list as CSV'],
  },
  {
    category:'REGISTRY & GIFTS', icon:Gift, color:'#C4826A', bg:'rgba(196,130,106,0.12)',
    title:'Registry & Digital Cash Gifts',
    desc:'A built-in wishlist guests can browse and reserve — plus digital cash gifts that go straight to your own bank. Okoiya never holds the money; you just keep a small service fee out of it.',
    bullets:['Guests send cash gifts online — it settles directly to your verified bank account','You set up your payout account once; it covers both cash gifts and Aso Ebi','A small service fee is added on top, so you receive the full amount gifted','Cash fund items with a target amount and progress bar that updates as gifts come in','Every gift auto-populates your Gift Log with one-tap WhatsApp thank-yous'],
  },
  {
    category:'ASO EBI', icon:Shirt, color:'#9BAF9B', bg:'rgba(155,175,155,0.15)',
    title:'Aso Ebi Coordination',
    desc:'The first fully digital Aso Ebi system built for Nigerian weddings. Guests order and pay online, orders confirm themselves, and the fabric money settles straight to your bank.',
    bullets:['Guests order and pay for fabric online — it settles directly to your bank','Orders mark themselves "paid" automatically once payment clears — no manual chasing','Style lookbook with approved and Do Not Wear references','Multiple collection points, with a secure coordinator link to manage pickups','Track collection and send "come collect" WhatsApp reminders'],
  },
  {
    category:'STORY', icon:Heart, color:'#C4826A', bg:'rgba(196,130,106,0.12)',
    title:'Your Love Story',
    desc:'Share your story with your guests — His side and Her side, told separately. Set to your song. Woven through with your studio photos.',
    bullets:['Guided template with six story sections','His and Her perspectives shown side by side','Photos uploaded inline through the narrative','Add your song via Spotify or YouTube link','Published on your guest portal under Our Story'],
  },
  {
    category:'PHOTOS', icon:Camera, color:'#9BAF9B', bg:'rgba(155,175,155,0.15)',
    title:'Photo Gallery — Two Galleries, One Portal',
    desc:'Studio and pre-wedding shots curated by you. Guest candids uploaded from their phones on the day. Both galleries live on your wedding portal.',
    bullets:['Studio gallery curated and ordered by the couple','Guest photo uploads via QR code — no app needed','Host moderation toggle before guest photos go live (Premium)','Individual photo download from the gallery','Download full guest photo album as ZIP after the wedding'],
  },
  {
    category:'BUDGET', icon:DollarSign, color:'#C4826A', bg:'rgba(196,130,106,0.12)',
    title:'Budget Tracker',
    desc:'Every naira tracked. Every category live. See exactly what you have allocated, committed, and paid — with color-coded health indicators across every category.',
    bullets:['Category-based budget with planned vs actual tracking','Deposit and outstanding balance per vendor','Over-budget alerts per category — not just the total','Export full budget as CSV','Supports NGN, USD, GBP, EUR, GHS, KES, and ZAR'],
  },
  {
    category:'WEDDING DAY', icon:Tv, color:'#9BAF9B', bg:'rgba(155,175,155,0.15)',
    title:'Wedding Day Command Centre',
    desc:'On your wedding day Okoiya transforms into a live operations page — guest check-in, live streaming, and real-time arrival tracking all in one place.',
    bullets:['Live guest check-in with QR scan or manual search','Separate arrival count per event','Delegate check-in to a coordinator without sharing login','Live stream link for diaspora guests — YouTube, Zoom, Meet','Real-time auto-updating via live database subscription'],
  },
  {
    category:'GUEST PORTAL', icon:Globe, color:'#C4826A', bg:'rgba(196,130,106,0.12)',
    title:'The Guest Experience',
    desc:'Guests open one link — their personal invitation — and get everything. RSVP, programme, registry, love story, photos, Aso Ebi. No downloads. No accounts. Ever.',
    bullets:['Opens with a wax-sealed envelope animation','Tabs: Home, Our Story, Events, Programme, Registry, Aso Ebi, Studio, Through Your Eyes','Blessing Wall — guests leave prayers, wishes, testimonies','Password protection option for private weddings','Fully mobile-optimized — most guests use their phones'],
  },
  {
    category:'INVITATIONS', icon:Mail, color:'#9BAF9B', bg:'rgba(155,175,155,0.15)',
    title:'Digital Invitations',
    desc:'Beautifully designed, culturally-inspired invitation cards — personalised with your names, date and details, then sent as an image or a link. No designer needed.',
    bullets:['3 elegant templates free, plus designed premium cards','Personalise names, date, venue and your details','Download as a high-resolution image to share anywhere','Send straight to guests on WhatsApp','Upload your own custom invitation design (Premium)'],
  },
  {
    category:'VOWS & SPEECHES', icon:Feather, color:'#C4826A', bg:'rgba(196,130,106,0.12)',
    title:'Vow & Speech Writer',
    desc:"Stuck on what to say? Guided prompts turn your memories and promises into a heartfelt draft — vows for the couple, and speeches for the best man, maid of honour and hosts.",
    bullets:['Guided templates for bride & groom vows','Speech drafts for the best man, maid of honour and hosts','Answer a few prompts — get a polished, personal draft','Edit and save privately, revisit any time','Copy or download when you’re ready for the big day'],
  },
  {
    category:'MEMORIES', icon:Sparkles, color:'#9BAF9B', bg:'rgba(155,175,155,0.15)',
    title:'Okoiya Wrapped',
    desc:"When it's all over, Okoiya turns your wedding into a beautiful, animated recap — your guests, your gifts, your journey — a Wrapped made just for the two of you, ready to share.",
    bullets:['A full-screen, animated story of your wedding','Watch your guest count, gifts and journey come to life','A unique colour theme generated for your wedding','Set to music, swipeable like your favourite stories','Share a public version — your private details stay private'],
  },
]

function FeatureCard({ color, bg, Icon }) {
  return (
    <div style={{ background:`linear-gradient(135deg,${bg.replace('0.12)','0.25)').replace('0.15)','0.3)')},white)`, borderRadius:'1.5rem', padding:'3rem', display:'flex', alignItems:'center', justifyContent:'center', minHeight:280, border:'1px solid #E5E0D8' }}>
      <div style={{ width:100, height:100, background:bg, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center' }}>
        <Icon size={48} color={color}/>
      </div>
    </div>
  )
}

import { useSeo } from '../lib/seo'

export default function FeaturesPage() {
  useSeo({ title: 'Every Feature Your Wedding Needs', description: 'Multi-event management, smart RSVPs, Aso Ebi coordination, registry, budget tracker, vow writer and more — built for how Nigerian weddings really work.', path: '/features' })
  const live = useSiteLive()
  const preLaunch = live === false   // sealed: use minimal chrome, no site nav
  return (
    <div style={{ background:'#FAFAF8', minHeight:'100vh' }}>
      {preLaunch ? <LaunchHeader/> : <MarketingHeader/>}

      {/* Hero */}
      <section className="feat-hero-pad" style={{ textAlign:'center', padding:'5rem 2rem 4rem', maxWidth:800, margin:'0 auto' }}>
        <div style={{ display:'inline-flex', alignItems:'center', gap:'0.4rem', background:'rgba(155,175,155,0.15)', border:'1px solid rgba(155,175,155,0.3)', borderRadius:100, padding:'0.35rem 1rem', marginBottom:'1.5rem' }}>
          <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:700, color:'#7A917A', letterSpacing:'0.06em' }}>EVERYTHING YOU NEED</span>
        </div>
        <h1 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'clamp(2.2rem,5vw,3.5rem)', color:'#1A1A1A', margin:'0 0 1.25rem', lineHeight:1.15 }}>
          Every feature your wedding deserves
        </h1>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'1.05rem', color:'#6B6B6B', lineHeight:1.8, margin:'0 auto', maxWidth:640 }}>
          Okoiya is not a checklist app with a wedding skin. It was built from the ground up for how Nigerian weddings actually work — multiple events, Aso Ebi, large guest lists, diaspora guests, and all.
        </p>
      </section>

      {/* Feature sections — alternating */}
      <div className="feat-section-wrap" style={{ maxWidth:1100, margin:'0 auto', padding:'0 2rem 4rem' }}>
        {FEATURES.map(({ category, icon:Icon, color, bg, title, desc, bullets }, i) => (
          <div key={i} className="feat-section" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'4rem', alignItems:'center', padding:'4rem 0', borderBottom:i<FEATURES.length-1?'1px solid #E5E0D8':'none' }}>

            {/* Visual — alternates sides */}
            {i%2===0 && <div className="feat-visual"><FeatureCard color={color} bg={bg} Icon={Icon}/></div>}

            {/* Text */}
            <div className="feat-text" style={{ order:i%2===0?1:0 }}>
              <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.7rem', fontWeight:700, color:color, letterSpacing:'0.12em', textTransform:'uppercase' }}>{category}</span>
              <h2 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'2rem', color:'#1A1A1A', margin:'0.5rem 0 0.875rem', lineHeight:1.2 }}>{title}</h2>
              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.95rem', color:'#6B6B6B', lineHeight:1.8, margin:'0 0 1.5rem' }}>{desc}</p>
              <div style={{ display:'flex', flexDirection:'column', gap:'0.625rem' }}>
                {bullets.map((b,j)=>(
                  <div key={j} style={{ display:'flex', gap:'0.625rem', alignItems:'flex-start' }}>
                    <div style={{ width:18, height:18, borderRadius:'50%', background:'rgba(196,130,106,0.12)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:2 }}>
                      <span style={{ fontSize:'0.6rem', color:'#C4826A' }}>✓</span>
                    </div>
                    <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.875rem', color:'#3A3A3A', lineHeight:1.6 }}>{b}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Visual — alternates sides */}
            {i%2!==0 && <div className="feat-visual" style={{order:1}}><FeatureCard color={color} bg={bg} Icon={Icon}/></div>}
          </div>
        ))}
      </div>

      {/* CTA */}
      <section style={{ padding:'2rem 2rem 5rem' }}>
        <div className="feat-cta" style={{ background:'linear-gradient(135deg,#C4826A 0%,#D4A882 50%,#9BAF9B 100%)', borderRadius:'2rem', maxWidth:1100, margin:'0 auto', padding:'5rem 2rem', textAlign:'center' }}>
          <h2 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'clamp(2rem,5vw,3rem)', color:'white', margin:'0 0 1rem' }}>{preLaunch ? 'Be one of the first' : 'See it all in action'}</h2>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'1rem', color:'rgba(255,255,255,0.85)', margin:'0 0 2.5rem', lineHeight:1.7 }}>
            {preLaunch
              ? 'Okoiya opens soon. Join the founding couples and be first through the doors.'
              : 'Start free and explore every feature at your own pace. No credit card. No commitment.'}
          </p>
          <Link to={preLaunch ? '/waitlist' : '/signup'}>
            <button style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'1rem', color:'#1A1A1A', background:'white', border:'none', borderRadius:100, padding:'1rem 2.5rem', cursor:'pointer' }}>
              {preLaunch ? 'Join the waitlist →' : 'Start Planning Free →'}
            </button>
          </Link>
        </div>
      </section>

      {preLaunch ? <LaunchFooter/> : <MarketingFooter/>}

      <style>{`
        @keyframes floatCouple { 0%,100%{transform:translateY(0)}50%{transform:translateY(-14px)} }
        @media (max-width:768px) {
          .feat-section { grid-template-columns: 1fr !important; gap: 2rem !important; }
          .feat-visual   { order: 0 !important; }
          .feat-text     { order: 1 !important; }
          .feat-hero-pad { padding: 3rem 1.25rem 2rem !important; }
          .feat-section-wrap { padding: 0 1.25rem 2rem !important; }
          .feat-section  { padding: 2rem 0 !important; }
          .feat-cta      { padding: 3rem 1.25rem !important; border-radius: 1.25rem !important; }
          .feat-cta h2   { font-size: 1.75rem !important; }
        }
      `}</style>
    </div>
  )
}
